/**
 * How long is left on an upsell campaign (kitecorp/transferify-ui#1448, api
 * kitecorp/transferify-api-service#933).
 *
 * React-free, like every rule in this module: a countdown is arithmetic over two instants, and the only
 * hard parts — which instant the deadline actually is, and when to say nothing at all — are the parts a
 * component must not be inventing for itself.
 *
 * <b>The api sends a DATE, not an instant, and the difference is the whole design.</b> `endsOn` is the
 * LAST DAY the campaign is open, decided server-side in UTC (the api, its Postgres and every deployed
 * task all run UTC). So the moment it shuts is the START of the following day in UTC — not the end of
 * that day in the reader's own zone, which for a reader in UTC+3 would be three hours late and would
 * keep a timer ticking on an offer the checkout had already stopped honouring.
 *
 * <b>Nothing here fabricates a deadline.</b> A campaign with no `endsOn` is one that never closes, and
 * the honest render for that is no countdown at all — see {@link campaignCountdown} returning null. An
 * always-on "ends soon" is the false-urgency claim this whole feature was told to avoid.
 */

/** What is left, already broken into the units a countdown renders. */
export interface CampaignCountdown {
	/** Whole days remaining. */
	days: number;
	/** Whole hours past those days, 0-23. */
	hours: number;
	/** Whole minutes past those hours, 0-59. */
	minutes: number;
	/** Total milliseconds remaining — the one value a caller should compare, never the parts. */
	totalMs: number;
}

const MS_PER_MINUTE = 60_000;
const MS_PER_HOUR = 60 * MS_PER_MINUTE;
const MS_PER_DAY = 24 * MS_PER_HOUR;

/**
 * The instant a campaign whose last open day is {@code endsOn} actually shuts.
 *
 * <b>Start of the NEXT day, UTC.</b> `endsOn` names a day the offer is still open, so the deadline is
 * the first instant it is not — midnight UTC at the end of it. `Date.UTC` rather than a local-zone
 * constructor: the same wall-clock date must mean the same instant for every reader, or two people
 * looking at one campaign see different amounts of time left.
 *
 * @param endsOn an ISO date, `YYYY-MM-DD`, exactly as the api sends it
 * @return the closing instant in epoch milliseconds, or null when the date cannot be read
 */
export function campaignClosesAt(endsOn: string | null | undefined): number | null {
	if (typeof endsOn !== "string") {
		return null;
	}
	// Matched rather than handed to `Date.parse`: that accepts a great many shapes, several of which it
	// interprets in the LOCAL zone, and a silently local-zone deadline is the exact defect this module's
	// header is about. Only the api's own `YYYY-MM-DD` is accepted.
	const parts = /^(\d{4})-(\d{2})-(\d{2})$/.exec(endsOn.trim());
	if (parts === null) {
		return null;
	}
	const [year, month, day] = [Number(parts[1]), Number(parts[2]), Number(parts[3])];
	const closes = Date.UTC(year, month - 1, day + 1);
	// Round-tripped to reject a date that does not exist. `Date.UTC(2026, 1, 30)` silently rolls into
	// March rather than failing, so a garbled `2026-02-30` would otherwise produce a real-looking
	// deadline a day and a half from where the operator meant it.
	const rolled = new Date(Date.UTC(year, month - 1, day));
	if (rolled.getUTCFullYear() !== year || rolled.getUTCMonth() !== month - 1
			|| rolled.getUTCDate() !== day) {
		return null;
	}
	return closes;
}

/**
 * What is left of the campaign, or null when there is nothing honest to show.
 *
 * <b>Null covers three different situations on purpose</b>, because they share one right answer —
 * render no timer:
 *
 * <ul>
 *   <li>no {@code endsOn}: an always-on campaign, which has no deadline to count to;</li>
 *   <li>an unreadable {@code endsOn}: we cannot state when it shuts, so we do not;</li>
 *   <li>a deadline already past: the offer is over. A timer reading "0d 0h" on a live callout tells the
 *       reader the opposite of the truth, and the clocks involved (the reader's, ours) can legitimately
 *       disagree by minutes, so this case is reached in normal operation rather than only in tests.</li>
 * </ul>
 *
 * @param endsOn the campaign's last open day, as the api sends it
 * @param nowMs  the reader's current instant in epoch milliseconds
 */
export function campaignCountdown(
	endsOn: string | null | undefined, nowMs: number,
): CampaignCountdown | null {
	const closesAt = campaignClosesAt(endsOn);
	if (closesAt === null || !Number.isFinite(nowMs)) {
		return null;
	}
	const totalMs = closesAt - nowMs;
	if (totalMs <= 0) {
		return null;
	}
	return {
		days: Math.floor(totalMs / MS_PER_DAY),
		hours: Math.floor(totalMs % MS_PER_DAY / MS_PER_HOUR),
		minutes: Math.floor(totalMs % MS_PER_HOUR / MS_PER_MINUTE),
		totalMs,
	};
}

/**
 * How long until the rendered countdown would next CHANGE, in milliseconds.
 *
 * <p>Lets a component tick on the minute boundary rather than on a fixed interval: a one-second timer
 * re-renders sixty times for every visible change, and a one-minute timer drifts until the displayed
 * minute is up to fifty-nine seconds stale. Bounded below by a second so a caller cannot busy-loop on a
 * deadline that is about to pass.
 *
 * @param countdown the countdown currently on screen
 */
export function msUntilCountdownChanges(countdown: CampaignCountdown): number {
	const intoMinute = countdown.totalMs % MS_PER_MINUTE;
	return Math.max(intoMinute === 0 ? MS_PER_MINUTE : intoMinute, 1_000);
}

/**
 * Copy for the countdown sentence.
 *
 * Declared as a parameter rather than imported, so this module stays free of the message catalogs
 * exactly as it is free of React: how long is left is a rule, the words are copy, and each catalog
 * writes its own ("3 days", "3 zile").
 */
export interface CountdownDaysCopy {
	remainingWholeDays: (days: number) => string;
}

/**
 * How long is left, in inclusive days — the ONE countdown rendering every offer surface uses.
 *
 * A partial UTC day counts as a day because `endsOn` is the campaign's last OPEN day. A reader on
 * 28 August therefore sees four days when the campaign closes at the start of 1 September, and every
 * moment on 31 August reads as one day.
 *
 * <b>There is deliberately no second, finer formatter.</b> The claim dialog used to render its own
 * days-to-hours-to-minutes label (ui#1654 moved the callouts here and left the dialog behind), so a
 * reader who opened the dialog from a pill reading "3 days" was met with "2d 10h" — the same deadline,
 * two answers, which is worse than either on its own. Anything that needs the remaining time reads it
 * from here; a surface wanting its own unit is a decision to take once, for all of them.
 */
export function countdownDaysLabel(countdown: CampaignCountdown, copy: CountdownDaysCopy): string {
	return copy.remainingWholeDays(Math.ceil(countdown.totalMs / MS_PER_DAY));
}
